import { addCalendarDays, calendarDaysBetween } from './dates';

/**
 * Guided programs (batch 3 of the Manifest AI gap work): an N-day journey a
 * member enrols in. Each day is a short read, one thing to ask Master Kala,
 * and one micro-action that becomes that day's #1 action in the ordinary
 * plan, so the day is scored the way every day is.
 *
 * The built-in program ships as CODE so the safety grep in `programs.test.ts`
 * covers its copy, and is SEEDED into `coaching_programs` rows on first read
 * (never overwriting a row that already carries its key). After that it is
 * rows like any tenant-written program: editable, hideable, joined by id.
 *
 * Day N unlocks on `startedOn + (N - 1)` in the member's own calendar. Late
 * is fine, early is not. Day 1's read is open the moment a member enrols even
 * when its action is set for tomorrow (see `services/coaching/programs.ts`).
 */

export const COACHING_PROGRAM_MEDIA_KINDS = ['none', 'audio', 'video'] as const;
export type CoachingProgramMediaKind = (typeof COACHING_PROGRAM_MEDIA_KINDS)[number];

export const COACHING_PROGRAM_ENROLLMENT_STATUSES = ['active', 'completed', 'left'] as const;
export type CoachingProgramEnrollmentStatus = (typeof COACHING_PROGRAM_ENROLLMENT_STATUSES)[number];

/** What a day looks like from where the member stands today. */
export const COACHING_PROGRAM_DAY_STATES = ['done', 'today', 'available', 'locked'] as const;
export type CoachingProgramDayState = (typeof COACHING_PROGRAM_DAY_STATES)[number];

/** Bounds an admin-written program must stay inside (the zod schema and the CHECKs repeat them). */
export const COACHING_PROGRAM_LIMITS = {
  daysMax: 90,
  titleMax: 80,
  taglineMax: 140,
  descriptionMax: 1000,
  dayTitleMax: 80,
  lessonMax: 4000,
  promptMax: 600,
  actionTitleMax: 120,
  actionMinutesMax: 240,
} as const;

export type BuiltInProgramDay = {
  dayNo: number;
  title: string;
  /** The read: paragraphs separated by a blank line, bold and lists as Master Kala's RichText renders them. */
  lesson: string;
  /** What lands in the composer when the member taps "Ask Master Kala about this". */
  kalaPrompt: string;
  /** The micro-action, written as a `coaching_actions` title. */
  actionTitle: string;
  actionMinutes: number;
};

export type BuiltInProgram = {
  key: string;
  title: string;
  tagline: string;
  description: string;
  days: BuiltInProgramDay[];
};

export const SEVEN_DAY_START_KEY = 'seven-day-start';

const SEVEN_DAY_START: BuiltInProgram = {
  key: SEVEN_DAY_START_KEY,
  title: 'Your first seven days',
  tagline: 'One small step a day, and a rhythm that lasts.',
  description:
    'Seven short days that walk you through the app the way it is meant to be used: a morning that sets you up, one action that matters, and an evening that closes the loop. Each day is a short read, one question for Master Kala, and one thing to do.',
  days: [
    {
      dayNo: 1,
      title: 'Your starting point',
      lesson:
        'Welcome. Before anything else, notice where you are today, without judging it.\n\nThis week is not about doing more. It is about doing one thing on purpose each day and letting the rest follow. The Success Score on your Home screen measures what you do here: the morning ritual, your #1 action, a mindset practice, an alignment practice and the evening review. Nothing on it predicts an outcome. It shows you a day, honestly.\n\nToday, open the morning ritual and go through it once at your own pace. That is the whole day.',
      kalaPrompt:
        'This is my first day with the app. Ask me what I want to be different in seven days, then reflect it back to me in one sentence.',
      actionTitle: 'Complete the morning ritual once',
      actionMinutes: 10,
    },
    {
      dayNo: 2,
      title: 'One goal, one step',
      lesson:
        'A goal is a direction. An action is a step you can finish today. Most days go sideways when the two get mixed up: we hold the big picture in mind and then do nothing about it.\n\nOpen Achieve and look at your primary goal and today’s #1 action. If the action is too big to finish today, make it smaller until it fits. Small and done beats large and postponed.\n\nToday’s task is simply to do that #1 action, then mark it done.',
      kalaPrompt:
        'Look at my primary goal and today’s #1 action. Is the action small enough to finish today? If not, help me shrink it, and propose the smaller version.',
      actionTitle: 'Do today’s #1 action and mark it done',
      actionMinutes: 30,
    },
    {
      dayNo: 3,
      title: 'Guard your morning',
      lesson:
        'The first hour of the day sets the tone for the rest. Most distractions do not announce themselves; they arrive as a phone in the hand before the feet touch the floor.\n\nA guard is a small rule you can keep: the phone stays face down until the ritual is done, or the first hour holds one task and nothing else. Choose a rule you can honour on a bad day, not only on a perfect one.\n\nToday, try one short breathing session in Align before you open anything else.',
      kalaPrompt:
        'Help me choose one small guard for my first hour tomorrow, something I can keep even on a rough day.',
      actionTitle: 'Do one breathing session before opening messages',
      actionMinutes: 5,
    },
    {
      dayNo: 4,
      title: 'Money, honestly',
      lesson:
        'Money is a tool, and a tool is easier to use when you look at it plainly. This app gives no financial advice and never will; what it offers is a place to think clearly.\n\nToday’s practice is gratitude, not arithmetic. In your journal, write three things money already makes possible in your life this week. Small things count: the ride to work, the meal you shared, the light you read by.\n\nNotice how it feels to name them. That feeling is the ground you plan from.',
      kalaPrompt:
        'Guide me through a short money gratitude practice: three things money already makes possible in my life this week. Then ask me one question about what I want it to make possible next.',
      actionTitle: 'Write three things money makes possible, in the journal',
      actionMinutes: 10,
    },
    {
      dayNo: 5,
      title: 'The people around you',
      lesson:
        'Nobody builds alone. The people who supported you this week did it without being asked, and most of them never hear that it mattered.\n\nToday, send one honest message of appreciation. No request attached, no selling, nothing to follow up. Just thanks, in your own words, to one person.\n\nIf you lead a team, make it someone on your team.',
      kalaPrompt:
        'Help me write a short, honest message of appreciation to [name] for [what they did]. No selling, no request, just thanks.',
      actionTitle: 'Send one message of thanks with nothing attached',
      actionMinutes: 5,
    },
    {
      dayNo: 6,
      title: 'Meet your Future Self',
      lesson:
        'Your Future Self is not a fantasy. It is a description you write of the person you are choosing to become, and written down it turns from a vague wish into something you can consult.\n\nOpen Manifest, then Future Self, and answer one prompt for the horizon that feels closest. One honest paragraph is enough. You can add the rest another day.\n\nMaster Kala reads what you write there, so from now on you can ask what that version of you would do.',
      kalaPrompt:
        'I have started writing my Future Self. Ask me one question that would help me describe that person more clearly, then tell me what you noticed.',
      actionTitle: 'Answer one Future Self prompt',
      actionMinutes: 10,
    },
    {
      dayNo: 7,
      title: 'Close the loop',
      lesson:
        'A week has a shape when it has a review at the end. The evening review closes the day: what worked, what did not, how you felt, and one priority for tomorrow. Done daily, it is what turns effort into learning.\n\nTonight, do the evening review with care. Then ask Master Kala for one focus for next week, based on this one.\n\nThat is the rhythm: a morning that sets you up, one action that matters, an evening that closes the loop. Keep it, and the rest follows.',
      kalaPrompt:
        'This is the last day of my first week. Based on what you know about my week, help me choose one focus for next week and propose it as an action for tomorrow.',
      actionTitle: 'Do the evening review and set tomorrow’s priority',
      actionMinutes: 10,
    },
  ],
};

export const BUILT_IN_PROGRAMS: readonly BuiltInProgram[] = [SEVEN_DAY_START];

export const BUILT_IN_PROGRAM_KEYS = BUILT_IN_PROGRAMS.map((p) => p.key);

export function isBuiltInProgramKey(key: string | null): boolean {
  return key !== null && BUILT_IN_PROGRAM_KEYS.includes(key);
}

/**
 * Which day of a program is due on `day` for a member who started on
 * `startedOn`: 1 on the start day, 2 the day after, and so on. 0 before the
 * start (a program that begins tomorrow), and past `daysCount` once it is over.
 */
export function programDayDue(startedOn: string, day: string): number {
  return calendarDaysBetween(startedOn, day) + 1;
}

/** The calendar day on which `dayNo` unlocks. */
export function programDayDate(startedOn: string, dayNo: number): string {
  return addCalendarDays(startedOn, dayNo - 1);
}

/**
 * How one day reads from `day`: done when its lesson is marked, otherwise
 * today / available (behind the member, still open) / locked (ahead). Day 1 is
 * open from enrolment even when the program starts tomorrow, so a member who
 * has just joined has something to read.
 */
export function programDayState(args: {
  dayNo: number;
  startedOn: string;
  day: string;
  lessonDone: boolean;
}): CoachingProgramDayState {
  if (args.lessonDone) return 'done';
  const due = programDayDue(args.startedOn, args.day);
  if (args.dayNo === 1 && due <= 1) return 'today';
  if (args.dayNo === due) return 'today';
  return args.dayNo < due ? 'available' : 'locked';
}
